import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef } from "react";
import { useKeyedState } from "@/hooks/useKeyedState";
const ROW_GAP_PX = 16; // gap-4 between tabs viewport and info strip
const HIDE_WHEN_OVERFLOW_PX = 1;
/** Extra slack so we do not immediately re-show the strip when width is borderline. */
const SHOW_ROW_BUFFER_PX = 12;
const DEFAULT_INFO_STRIP_WIDTH_PX = 400;

/**
 * Hides the job/run metadata strip (InfoCard) when the pill tab row does not have
 * enough width for the tab labels — i.e. the strip is crowding the tabs into overflow.
 * Re-shows only when the row is wide enough for both (hysteresis to avoid flicker).
 */
export function useInfoStripCollidesWithTabs(t0) {
  const $ = _c(6);
  const enabled = t0 === undefined ? true : t0;
  const rowRef = useRef(null);
  const tabsViewportRef = useRef(null);
  const pillTabsListRef = useRef(null);
  const infoStripRef = useRef(null);
  const lastInfoStripWidthRef = useRef(DEFAULT_INFO_STRIP_WIDTH_PX);
  const [showInfoStrip, setShowInfoStrip] = useKeyedState(enabled, true);
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== setShowInfoStrip) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      const row = rowRef.current;
      const viewport = tabsViewportRef.current;
      const list = pillTabsListRef.current;
      if (!row || !viewport || !list) {
        return;
      }
      const measure = () => {
        const pillNeeds = list.scrollWidth;
        const tabsAvail = viewport.clientWidth;
        if (tabsAvail <= 0 || pillNeeds <= 0) {
          return;
        }
        setShowInfoStrip(prev => {
          if (prev) {
            if (pillNeeds > tabsAvail + HIDE_WHEN_OVERFLOW_PX) {
              const w = infoStripRef.current?.getBoundingClientRect().width;
              if (w && w > 0) {
                lastInfoStripWidthRef.current = w;
              }
              return false;
            }
            return prev;
          }
          const rowW = row.clientWidth;
          const minRow = pillNeeds + lastInfoStripWidthRef.current + ROW_GAP_PX + SHOW_ROW_BUFFER_PX;
          return rowW >= minRow ? true : prev;
        });
      };
      measure();
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      let rafId = null;
      const scheduleMeasure = () => {
        if (rafId != null) {
          return;
        }
        rafId = requestAnimationFrame(() => {
          rafId = null;
          measure();
        });
      };
      const ro = new ResizeObserver(scheduleMeasure);
      ro.observe(row);
      ro.observe(viewport);
      ro.observe(list);
      return () => {
        if (rafId != null) {
          cancelAnimationFrame(rafId);
        }
        ro.disconnect();
      };
    };
    t2 = [enabled, setShowInfoStrip];
    $[0] = enabled;
    $[1] = setShowInfoStrip;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[4] !== showInfoStrip) {
    t3 = {
      rowRef,
      tabsViewportRef,
      pillTabsListRef,
      infoStripRef,
      showInfoStrip
    };
    $[4] = showInfoStrip;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}